import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { roleLabels } from "../lib/format";
import { Modal, Field } from "./UI";
import {
  Bell, Menu, LogOut, X, LayoutDashboard, Box, ShoppingCart, Truck, ChevronRight,
  ClipboardCheck, Calculator, Flame, Coins, Users, UserCog, FileBarChart, ClipboardList, KeyRound, PackageOpen, Settings, BookOpen,
} from "lucide-react";

const BASE_NAV = [
  ["/dashboard", "Overview", LayoutDashboard],
  ["/inventory", "Items", Box],
  ["/suppliers", "Supplier", Users],
  ["/purchase-requests", "Purchase request", ClipboardList],
  ["/store-requests", "Store request", PackageOpen],
  ["/orders", "Purchase order", ShoppingCart],
  ["/receiving", "Receiving", Truck],
  ["/issues", "Stock out", ChevronRight],
  ["/opname", "Stock take", ClipboardCheck],
  ["/coa", "Chart of Accounts", BookOpen],
  ["/hpp", "COGS & recipes", Calculator],
  ["/revenue", "Daily revenue", Coins],
  ["/flash", "Flash cost", Flame],
  ["/reports", "Reports", FileBarChart],
];

const ADMIN_NAV = [
  ["/users", "User management", UserCog],
  ["/settings", "System settings", Settings],
];

const REQUESTER_NAV = [
  ["/purchase-requests", "Purchase request", ClipboardList],
  ["/store-requests", "Store request", PackageOpen],
];

export default function Layout({
  outletCode,
  onOutletChange,
  outlets,
  pendingPo,
  pendingPr,
  pendingSr,
  children,
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [showPwd, setShowPwd] = React.useState(false);
  const NAV =
    user?.role === "requester"
      ? REQUESTER_NAV
      : user?.role === "admin"
      ? [...BASE_NAV, ...ADMIN_NAV]
      : BASE_NAV;
  const badgeCount = {
    "/orders": pendingPo,
    "/purchase-requests": pendingPr,
    "/store-requests": pendingSr,
  };
  const badgeStatus = {
    "/orders": "waiting_approval",
    "/purchase-requests": "pending_approval",
    "/store-requests": "pending",
  };
  const active = NAV.find(([p]) => location.pathname.startsWith(p));
  const title = active?.[1] || "Overview";
  const activeId = active ? active[0].slice(1) : "dashboard";
  const initials = (user?.name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="app-shell">
      <aside className={`sidebar ${open ? "mobile-open" : ""}`} data-testid="app-sidebar">
        <div className="brand">
          <div className="brand-mark">L</div>
          <div>
            <strong>LAGO BALI</strong>
            <span>Inventory · F&B OS</span>
          </div>
          <button
            data-testid="mobile-close-button"
            className="icon-button mobile-close"
            onClick={() => setOpen(false)}
          >
            <X size={18} />
          </button>
        </div>
        <div className="nav-label">Workspace</div>
        <nav>
          {NAV.map(([path, label, Icon]) => (
            <NavLink
              to={path}
              key={path}
              data-testid={`nav-${path.slice(1)}`}
              className={({ isActive }) => (isActive ? "nav-item active" : "nav-item")}
              onClick={() => setOpen(false)}
            >
              <Icon size={17} />
              <span>{label}</span>
              {badgeCount[path] > 0 && (
                <em
                  data-testid={`nav-badge-${path.slice(1)}`}
                  title="Lihat yang menunggu persetujuan"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(false);
                    navigate(`${path}?status=${badgeStatus[path]}`);
                  }}
                >
                  {badgeCount[path]}
                </em>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div>
            <div className="live-dot" /> Connected
            <span>User: {user?.name}</span>
          </div>
          <button
            data-testid="change-password-button"
            className="chip small"
            onClick={() => setShowPwd(true)}
            style={{ marginTop: 12 }}
          >
            <KeyRound size={12} /> Change password
          </button>
          <button
            data-testid="logout-button"
            className="chip small"
            onClick={handleLogout}
            style={{ marginTop: 8 }}
          >
            <LogOut size={12} /> Logout
          </button>
          <div className="credit">Created by <strong>NANDA HARY</strong></div>
        </div>
      </aside>
      {open && (
        <button
          data-testid="mobile-menu-overlay"
          className="mobile-overlay"
          onClick={() => setOpen(false)}
          aria-label="Close menu"
        />
      )}
      <main className="main">
        <header className="topbar">
          <button
            data-testid="mobile-menu-button"
            className="mobile-menu"
            onClick={() => setOpen(true)}
          >
            <Menu size={20} />
          </button>
          <div className="crumb">
            <span>Lago Bali</span>
            <ChevronRight size={14} />
            <strong data-testid="page-title">{title}</strong>
          </div>
          <div className="top-actions">
            <label className="select-wrap">
              <span>Outlet</span>
              <select
                data-testid="outlet-selector"
                value={outletCode}
                onChange={(e) => onOutletChange(e.target.value)}
              >
                <option value="all">All outlets</option>
                {outlets.map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="select-wrap role-select">
              <span>Role</span>
              <strong data-testid="current-role">
                {roleLabels[user?.role] || user?.role}
              </strong>
            </label>
            <button data-testid="notifications-button" className="icon-button notification">
              <Bell size={18} />
              <i />
            </button>
            <div className="avatar" data-testid="current-user-avatar" title={user?.username}>
              {initials}
            </div>
          </div>
        </header>
        <div className="content" data-active-page={activeId}>{children}</div>
      </main>
      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = React.useState("");
  const [next, setNext] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next) return toast.error("Fill both current and new password");
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Confirmation does not match");
    if (next === current) return toast.error("New password must differ from current");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Change your password" onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <p className="form-hint" style={{ gridColumn: "1/-1" }}>
          Password must be at least 6 characters. You will stay signed in on this device.
        </p>
        <Field
          label="Current password"
          testid="change-password-current"
          type="password"
          value={current}
          onChange={setCurrent}
          required
        />
        <Field
          label="New password"
          testid="change-password-new"
          type="password"
          value={next}
          onChange={setNext}
          required
        />
        <Field
          label="Confirm new password"
          testid="change-password-confirm"
          type="password"
          value={confirm}
          onChange={setConfirm}
          required
        />
        <div className="form-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className="primary-button"
            disabled={saving}
            data-testid="change-password-submit"
          >
            {saving ? "Saving..." : "Update password"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
